import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import {
  subscribeToEvents as subscribeToEventsService,
  addEvent as addEventService,
  updateEventById as updateEventService,
  deleteEventById as deleteEventService,
} from "@/helpers/firestore/EventService";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { setErrorToast } from "@/helpers/notifications/toast";
import {
  getCachedDocuments,
  setCachedDocuments,
} from "@/helpers/cache/document-cache";
import type { EventDocument } from "@kekkle/shared/types/documents/event-document/event-document";
import { type FirestoreError } from "firebase/firestore";
import { useUserStore } from "@/stores/modules/user/user";
import { AgendaLayout } from "@/enums/agenda-layout";

type RangedEventDate = {
  start: Date;
  end: Date;
};

/**
 * Events store for managing calendar and list events per group.
 * Uses onSnapshot for real-time sync with Firestore + persistentLocalCache.
 * Only one active listener at a time (current group).
 */
export const useEventsStore = defineStore("events", () => {
  const userStore = useUserStore();

  /* Layout mode - persisted to localStorage */
  const layoutMode = ref<AgendaLayout>(
    (localStorage.getItem("agendaLayout") as AgendaLayout) ??
      AgendaLayout.Calendar,
  );

  function setLayoutMode(mode: AgendaLayout): void {
    layoutMode.value = mode;
    localStorage.setItem("agendaLayout", mode);
  }

  /* State - organized by group ID */
  const eventsByGroup = ref<Record<string, EventDocument[]>>({});
  const loadingByGroup = ref<Record<string, boolean>>({});
  const isStaleByGroup = ref<Record<string, boolean>>({});
  const selectedCalendarDate = ref<Date | null>(null);
  const calendarMonth = ref<Date>(new Date());
  const selectedCalendarEventId = ref<string | null>(null);
  const fetchError = ref<string | null>(null);

  /* Listener management - only one active at a time */
  let activeUnsubscribe: (() => void) | null = null;
  let subscribedGroupId: string | null = null;

  /* Computed / Getters - for active group */
  const activeGroupId = computed(() => userStore.activeGroup);

  const eventDocs = computed(() => {
    if (!activeGroupId.value) return [];
    return eventsByGroup.value[activeGroupId.value] ?? [];
  });

  const loadingEvents = computed(() => {
    if (!activeGroupId.value) return false;
    return loadingByGroup.value[activeGroupId.value] ?? false;
  });

  const isStale = computed(() => {
    if (!activeGroupId.value) return false;
    return isStaleByGroup.value[activeGroupId.value] ?? false;
  });

  const selectedCalendarEventDoc = computed(() => {
    return eventDocs.value.find(
      (eventDoc) => eventDoc._id === selectedCalendarEventId.value,
    );
  });

  const hasSelectedCalendarEvent = computed(() => {
    return (
      selectedCalendarEventId.value !== null &&
      selectedCalendarEventDoc.value !== undefined
    );
  });

  const currentMonthEvents = computed(() => {
    return eventDocs.value.filter((eventDoc) => {
      const beginEventDate = eventDoc.begin_timestamp;
      const endEventDate = eventDoc.end_timestamp;
      return (
        (beginEventDate.getMonth() === calendarMonth.value.getMonth() &&
          beginEventDate.getFullYear() === calendarMonth.value.getFullYear()) ||
        (endEventDate.getMonth() === calendarMonth.value.getMonth() &&
          endEventDate.getFullYear() === calendarMonth.value.getFullYear())
      );
    });
  });

  const currentDayEvents = computed(() => {
    if (!selectedCalendarDate.value) return [];
    return eventDocs.value.filter((eventDoc) => {
      const beginEventDate = eventDoc.begin_timestamp;
      const endEventDate = eventDoc.end_timestamp;
      return (
        (beginEventDate.getDate() === selectedCalendarDate.value!.getDate() &&
          beginEventDate.getMonth() ===
            selectedCalendarDate.value!.getMonth() &&
          beginEventDate.getFullYear() ===
            selectedCalendarDate.value!.getFullYear()) ||
        (endEventDate.getDate() === selectedCalendarDate.value!.getDate() &&
          endEventDate.getMonth() === selectedCalendarDate.value!.getMonth() &&
          endEventDate.getFullYear() ===
            selectedCalendarDate.value!.getFullYear())
      );
    });
  });

  const hasEvents = computed(() => eventDocs.value.length > 0);

  const eventDates = computed(() => {
    return eventDocs.value
      .filter(
        (event) =>
          event.begin_timestamp.toDateString() ===
          event.end_timestamp.toDateString(),
      )
      .map((event) => event.begin_timestamp);
  });

  const rangedEventDates = computed((): RangedEventDate[] => {
    return eventDocs.value
      .filter(
        (event) =>
          event.begin_timestamp.toDateString() !==
          event.end_timestamp.toDateString(),
      )
      .map((event) => ({
        start: event.begin_timestamp,
        end: event.end_timestamp,
      }));
  });

  /* Listener lifecycle */
  function subscribe(groupId: string): void {
    if (subscribedGroupId === groupId) return;

    unsubscribe();

    const hasInMemoryEvents = Boolean(eventsByGroup.value[groupId]?.length);

    if (!hasInMemoryEvents) {
      loadingByGroup.value[groupId] = true;
      // Immediately read from IndexedDB cache to populate the store instantaneously
      getCachedDocuments<EventDocument>("events", groupId)
        .then((cached) => {
          // Only populate from cache if server hasn't already responded for this group
          if (
            cached &&
            cached.length > 0 &&
            isStaleByGroup.value[groupId] !== false
          ) {
            eventsByGroup.value[groupId] = cached;
            loadingByGroup.value[groupId] = false;
            isStaleByGroup.value[groupId] = true;
          }
        })
        .catch((error) => {
          console.error("Error reading cached events from IndexedDB:", error);
        });
    }
    fetchError.value = null;

    activeUnsubscribe = subscribeToEventsService(groupId, {
      onEvents: (events, metadata) => {
        eventsByGroup.value[groupId] = events;
        loadingByGroup.value[groupId] = false;

        if (!metadata.fromCache) {
          // Fresh server response received
          isStaleByGroup.value[groupId] = false;
        } else if (isStaleByGroup.value[groupId] !== false) {
          // Cached response from Firestore local cache
          isStaleByGroup.value[groupId] = true;
        }

        // Persist fresh data to IndexedDB
        setCachedDocuments("events", groupId, events).catch((error) => {
          console.error("Error saving events to IndexedDB cache:", error);
        });
      },
      onError: (error) => {
        fetchError.value = handleFirestoreException({
          e: error as FirestoreError,
        });
        loadingByGroup.value[groupId] = false;
      },
    });

    subscribedGroupId = groupId;
  }

  function subscribeToEvents(): void {
    if (activeGroupId.value) {
      subscribe(activeGroupId.value);
    }
  }

  function unsubscribe(): void {
    if (activeUnsubscribe) {
      activeUnsubscribe();
      activeUnsubscribe = null;
    }
    subscribedGroupId = null;
  }

  /* Watch active group to maintain single listener */
  watch(
    activeGroupId,
    (newGroupId) => {
      if (newGroupId) {
        subscribe(newGroupId);
      } else {
        unsubscribe();
      }
    },
    { immediate: true },
  );

  /* Actions */
  function setSelectedCalendarEventId(eventId: string | null): void {
    selectedCalendarEventId.value = eventId;
  }

  function setCalendarMonthDate(date: Date): void {
    calendarMonth.value = date;
  }

  function setSelectedCalendarDate(date: Date | null): void {
    selectedCalendarDate.value = date;
  }

  function clearEventsForGroup(groupId: string): void {
    delete eventsByGroup.value[groupId];
    delete loadingByGroup.value[groupId];
    delete isStaleByGroup.value[groupId];
  }

  function reset(): void {
    unsubscribe();
    eventsByGroup.value = {};
    loadingByGroup.value = {};
    isStaleByGroup.value = {};
    selectedCalendarDate.value = null;
    selectedCalendarEventId.value = null;
    fetchError.value = null;
  }

  async function addEvent(data: Omit<EventDocument, "_id">): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      await addEventService(groupId, data);
      // onSnapshot listener receives the new event automatically
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function updateEvent(
    eventId: string,
    data: Partial<Omit<EventDocument, "_id">>,
  ): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      await updateEventService(eventId, groupId, data);
      // onSnapshot listener receives the updated event automatically
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  async function deleteEvent(eventId: string): Promise<void> {
    const groupId = activeGroupId.value;
    if (!groupId) return;

    try {
      await deleteEventService(eventId, groupId);
      if (selectedCalendarEventId.value === eventId) {
        selectedCalendarEventId.value = null;
      }
      // onSnapshot listener receives the deletion automatically
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    }
  }

  /* Exposed store */
  return {
    // State
    layoutMode,
    selectedCalendarDate,
    calendarMonth,
    eventDocs,
    selectedCalendarEventId,
    loadingEvents,
    isStale,
    isStaleByGroup,
    fetchError,

    // Computed
    selectedCalendarEventDoc,
    hasSelectedCalendarEvent,
    currentMonthEvents,
    currentDayEvents,
    hasEvents,
    eventDates,
    rangedEventDates,

    // Actions
    setLayoutMode,
    setSelectedCalendarEventId,
    setCalendarMonthDate,
    setSelectedCalendarDate,
    subscribeToEvents,
    unsubscribe,
    clearEventsForGroup,
    reset,
    addEvent,
    updateEvent,
    deleteEvent,
  };
});
